import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import Dialog, { DialogTitle, DialogContent, DialogActions } from "components/common/Dialog";
import Select from "components/common/Select";
import Text from "components/common/Text";
import Button from "components/common/Button";
import DragAndDrop from "components/common/DragAndDrop";
import DatePicker from "components/common/DatePicker";
import { getPayrollDocumentSignedUrl, uploadPayrollDocumentToS3 } from "services/Admin/Customer";

import "./UpdatePaymentStatusModal.scss";

const PAYMENT_METHOD_OPTIONS = [
  { value: "wire", label: "Wire Transfer" },
  { value: "cheque", label: "Check" },
];

const getStatusOptions = (currentStatus) => {
  if (currentStatus === "draft") {
    return [
      { value: "processing", label: "Processing" },
      { value: "paid", label: "Paid" },
      { value: "failed", label: "Failed" },
    ];
  }

  if (currentStatus === "processing") {
    return [
      { value: "paid", label: "Paid" },
      { value: "failed", label: "Failed" },
    ];
  }

  return [];
};

const getValidationSchema = () => {
  return yup.object().shape({
    paymentMethod: yup.string().required("Payment method is required"),
    status: yup.string().required("Status is required"),
    paymentDate: yup.date().required("Payment date is required").nullable(),
    paymentReference: yup.string().when(["paymentMethod", "status"], {
      is: (method, paymentStatus) => method === "wire" && paymentStatus === "paid",
      then: (schema) => schema.required("Payment reference is required when status is paid"),
      otherwise: (schema) => schema.notRequired(),
    }),
    proofOfPayment: yup.mixed().when(["paymentMethod"], {
      is: (method) => method === "cheque",
      then: (schema) => schema.required("Proof of payment is required for cheque payments"),
      otherwise: (schema) => schema.notRequired(),
    }),
    notes: yup.string().notRequired(),
  });
};

function UpdatePaymentStatusModal({
  open,
  onClose,
  onSubmit,
  paymentData = {},
  paymentHistoryData = {},
  isLoading = false,
  customerId: customerIdProp,
}) {
  const [fileObjects, setFileObjects] = useState([]);
  const [availableStatusOptions, setAvailableStatusOptions] = useState([]);
  const [uploadedUrls, setUploadedUrls] = useState([]);

  const {
    status = "",
    paymentDate = "",
    paymentMethod: initialPaymentMethod = "",
    customerId: customerIdFromData = "",
    customerPayrollId = "",
  } = paymentData;

  const customerId = customerIdProp || customerIdFromData;

  const {
    billingPeriod: historyBillingPeriod = "",
    paymentType: historyPaymentType = "",
    total: historyTotal = "",
  } = paymentHistoryData || {};

  const total = historyTotal || parseFloat(paymentData.totalPayroll || 0) + parseFloat(paymentData.totalBonus || 0);

  const billingPeriod = historyBillingPeriod
    ? new Date(`${historyBillingPeriod}-01`).toLocaleDateString("en-US", { month: "short", year: "numeric" })
    : "";
  const paymentType = historyPaymentType || "Regular";

  let currentStatus = status;
  if (status === "un-paid") {
    currentStatus = "draft";
  } else if (status === "payment_failed") {
    currentStatus = "failed";
  } else if (status === "pending") {
    currentStatus = "processing";
  }

  const methods = useForm({
    mode: "all",
    defaultValues: {
      paymentMethod: initialPaymentMethod || "wire",
      status: "",
      paymentDate: paymentDate ? new Date(paymentDate) : new Date(),
      paymentReference: "",
      notes: "",
      proofOfPayment: null,
    },
    resolver: yupResolver(getValidationSchema()),
  });

  const { handleSubmit, formState, watch, reset, setValue } = methods;
  const { isValid } = formState;
  const selectedPaymentMethod = watch("paymentMethod");
  const selectedStatus = watch("status");

  useEffect(() => {
    const options = getStatusOptions(currentStatus);
    setAvailableStatusOptions(options);

    if (!selectedStatus && options.length > 0) {
      setValue("status", options[0].value, { shouldValidate: true });
    } else if (selectedStatus && !options.find((opt) => opt.value === selectedStatus)) {
      setValue("status", options[0]?.value || "", { shouldValidate: true });
    }
  }, [currentStatus, selectedStatus, setValue]);

  const handleClose = () => {
    reset();
    setFileObjects([]);
    setUploadedUrls([]);
    setValue("proofOfPayment", null, { shouldValidate: false });
    onClose();
  };

  useEffect(() => {
    if (fileObjects.length < uploadedUrls.length) {
      const updatedUrls = uploadedUrls.filter((_, index) => index < fileObjects.length);
      setUploadedUrls(updatedUrls);
      setValue("proofOfPayment", updatedUrls.length > 0 ? updatedUrls : null, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileObjects.length]);

  const handleFormSubmit = async (data) => {
    const mapStatus = (statusValue) => {
      if (statusValue === "processing") return "pending";
      return statusValue;
    };

    const payload = {
      paymentMethod: data.paymentMethod,
      status: mapStatus(data.status),
      note: data.notes || "",
      referenceNumber: data.paymentReference || "",
      paymentDate: data.paymentDate ? data.paymentDate.toISOString() : new Date().toISOString(),
      documents: uploadedUrls,
    };

    await onSubmit(payload);
    handleClose();
  };

  const handleFileUpload = async (fileArray) => {
    if (!Array.isArray(fileArray) || fileArray.length === 0) return;

    await fileArray.reduce(async (previousPromise, fileObj) => {
      await previousPromise;
      try {
        const { file, id } = fileObj;
        const timestamp = Date.now();
        const s3Key = `${timestamp}-${file.name}`;

        const signedUrl = await getPayrollDocumentSignedUrl(customerId, customerPayrollId, s3Key, file.type, file.size);

        await uploadPayrollDocumentToS3(signedUrl, file);

        const s3Url = signedUrl.split("?")[0];

        setFileObjects((prevFiles) =>
          prevFiles.map((f) =>
            f.id === id
              ? {
                  ...f,
                  status: "complete",
                  progress: 100,
                }
              : f
          )
        );

        const documentInfo = {
          displayName: file.name,
          s3Key,
          size: file.size,
          path: s3Url,
          contentType: file.type,
        };

        setUploadedUrls((prev) => {
          const updated = [...prev, documentInfo];
          setValue("proofOfPayment", updated, { shouldValidate: true });
          return updated;
        });
      } catch (error) {
        setFileObjects((prevFiles) =>
          prevFiles.map((f) =>
            f.id === fileObj.id
              ? {
                  ...f,
                  status: "failed",
                  error: "Upload failed",
                  retry: true,
                }
              : f
          )
        );
      }
    }, Promise.resolve());
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatStatusText = (statusValue) => {
    if (!statusValue) return "";
    return statusValue
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getStatusClass = (statusValue) => {
    const statusClasses = {
      draft: "status-draft",
      pending: "status-pending",
      processing: "status-processing",
      paid: "status-paid",
      failed: "status-failed",
    };
    return statusClasses[statusValue] || "status-draft";
  };

  const showPaymentReference = selectedPaymentMethod === "wire";
  const showProofOfPayment = selectedPaymentMethod === "cheque";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="update-payment-status-modal"
      data-testid="update-payment-status-modal"
      size="sm"
      title={<DialogTitle title="Update Payment Status" />}
      content={
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <DialogContent>
              <div className="payment-summary-card">
                <div className="payment-summary-item">
                  <p className="paragraph text-sm font-medium line-height-sm payment-summary-label">Billing period</p>
                  <p className="paragraph text-lg font-medium line-height-xl payment-summary-value">{billingPeriod}</p>
                </div>
                <div className="payment-summary-item">
                  <p className="paragraph text-sm font-medium line-height-sm payment-summary-label">Payment type</p>
                  <p className="paragraph text-lg font-medium line-height-xl payment-summary-value">{paymentType}</p>
                </div>
                <div className="payment-summary-item">
                  <p className="paragraph text-sm font-medium line-height-sm payment-summary-label">Total</p>
                  <p className="paragraph text-lg font-medium line-height-xl payment-summary-value">
                    {formatCurrency(total)}
                  </p>
                </div>
                <div className="payment-summary-item">
                  <p className="paragraph text-sm font-medium line-height-sm payment-summary-label">Current status</p>
                  <p className={`paragraph text-lg font-medium line-height-xl ${getStatusClass(currentStatus)}`}>
                    {formatStatusText(currentStatus)}
                  </p>
                </div>
              </div>

              <div className="field-row">
                <div className="field-col">
                  <Select
                    label="Payment method"
                    options={PAYMENT_METHOD_OPTIONS}
                    name="paymentMethod"
                    placeholder="Select payment method"
                    size="small"
                  />
                </div>
                <div className="field-col">
                  <Select
                    label="Change status to"
                    options={availableStatusOptions}
                    name="status"
                    placeholder="Select status"
                    size="small"
                    disabled={availableStatusOptions.length === 0}
                  />
                </div>
              </div>

              <div className="field-margin-bottom">
                <DatePicker label="Payment date" name="paymentDate" placeholder="Select date" size="medium" />
              </div>

              {/* Payment Reference (Wire Transfer only) */}
              {showPaymentReference && (
                <div className="field-margin-bottom">
                  <Text
                    name="paymentReference"
                    label="Payment reference"
                    placeholder="Enter payment reference"
                    size="small"
                  />
                </div>
              )}

              {showProofOfPayment && (
                <div className="field-margin-bottom">
                  <p className="file-upload-label">Proof of payment</p>
                  <DragAndDrop
                    name="proofOfPayment"
                    supportedFormats={["application/pdf", "image/png", "image/jpeg", "image/jpg"]}
                    maxFileSize={3 * 1024 * 1024}
                    helperText="PDF, PNG, or JPG (max. 3MB)"
                    isMultiFileSupported
                    maximumFilesCount={2}
                    hideWhenMaxFilesReached
                    onFileSelect={handleFileUpload}
                    files={fileObjects}
                    setFiles={setFileObjects}
                  />
                </div>
              )}

              {/* Notes */}
              <div>
                <Text
                  name="notes"
                  multiline
                  rows={4}
                  label="Note"
                  isOptional
                  size="medium"
                  placeholder="Enter your note here..."
                />
              </div>
            </DialogContent>
          </form>
        </FormProvider>
      }
      actions={
        <DialogActions
          action1={<Button isDisabled={isLoading} variant="text" label="Cancel" size="large" onClick={handleClose} />}
          action2={
            <Button
              isLoading={isLoading}
              data-testid="submit-payment-status-button"
              onClick={handleSubmit(handleFormSubmit)}
              label={`Mark as ${formatStatusText(selectedStatus)}`}
              size="large"
              isDisabled={!isValid || !selectedStatus}
            />
          }
        />
      }
    />
  );
}

UpdatePaymentStatusModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  customerId: PropTypes.string,
  paymentData: PropTypes.shape({
    status: PropTypes.string,
    paymentDate: PropTypes.string,
    totalPayroll: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    totalBonus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paymentMethod: PropTypes.string,
    customerId: PropTypes.string,
    customerPayrollId: PropTypes.string,
  }),
  paymentHistoryData: PropTypes.shape({
    billingPeriod: PropTypes.string,
    paymentType: PropTypes.string,
    total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  isLoading: PropTypes.bool,
};

UpdatePaymentStatusModal.defaultProps = {
  paymentData: {
    status: "draft",
    paymentDate: "",
    totalPayroll: 0,
    totalBonus: 0,
    paymentMethod: "",
    customerId: "",
    customerPayrollId: "",
  },
  paymentHistoryData: {
    billingPeriod: "",
    paymentType: "",
    total: 0,
  },
  isLoading: false,
};

export default UpdatePaymentStatusModal;
